import React, { useState, useEffect } from "react";
import { 
  Container, 
  Typography, 
  Grid, 
  Box,
  Tabs,
  Tab,
  Chip,
  TextField,
  InputAdornment,
  Button,
  alpha,
  Paper,
  IconButton,
  Badge,
  Menu,
  MenuItem,
  FormControl,
  Select,
  Pagination,
  Skeleton,
  Breadcrumbs,
  Link,
  Tooltip,
  Zoom,
  Fade,
  Grow
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { motion, AnimatePresence } from "framer-motion";
import { Link as RouterLink } from "react-router-dom";
import CoinCard from "../components/CoinCard";
import { coins } from "../data/coins";
import SearchIcon from '@mui/icons-material/Search';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import FilterListIcon from '@mui/icons-material/FilterList';
import SortIcon from '@mui/icons-material/Sort';
import ViewComfyIcon from '@mui/icons-material/ViewComfy';
import ViewStreamIcon from '@mui/icons-material/ViewStream';
import CloseIcon from '@mui/icons-material/Close';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import NewReleasesIcon from '@mui/icons-material/NewReleases';
import StarIcon from '@mui/icons-material/Star';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import HomeIcon from '@mui/icons-material/Home';
import CheckIcon from '@mui/icons-material/Check';
import { useInView } from 'react-intersection-observer';

// Helper function to extract numeric price for sorting
const getNumericPrice = (coin) => {
  // Use priceNum if available (average price for sorting)
  if (coin.priceNum) return coin.priceNum;
  
  // If price is a string, extract the first number
  if (typeof coin.price === 'string') {
    const numbers = coin.price.match(/\d+(?:,\d+)*(?:\.\d+)?/g);
    if (numbers && numbers.length > 0) {
      return parseFloat(numbers[0].replace(/,/g, ''));
    }
  }
  
  // If price is a number
  if (typeof coin.price === 'number') return coin.price;
  
  return 0;
};

// Styled Components
const StyledTabs = styled(Tabs)(({ theme }) => ({
  marginBottom: theme.spacing(6),
  borderBottom: `1px solid ${alpha('#9E9E9E', 0.15)}`,
  '& .MuiTab-root': {
    color: '#616161',
    fontSize: '0.95rem',
    fontWeight: 500,
    textTransform: 'none',
    minWidth: 'auto',
    padding: '12px 24px',
    transition: 'all 0.2s ease',
    '&:hover': {
      color: theme.palette.primary.main,
      backgroundColor: alpha(theme.palette.primary.main, 0.04),
    },
    '&.Mui-selected': {
      color: theme.palette.primary.main,
      fontWeight: 600,
    },
  },
  '& .MuiTabs-indicator': {
    backgroundColor: theme.palette.primary.main,
    height: 3,
  },
}));

const StyledChip = styled(Chip)(({ theme, active }) => ({
  backgroundColor: active ? theme.palette.primary.main : 'transparent',
  color: active ? '#FFFFFF' : '#616161',
  border: active ? 'none' : `1px solid ${alpha('#9E9E9E', 0.3)}`,
  borderRadius: 0,
  fontWeight: 500,
  '&:hover': {
    backgroundColor: active ? theme.palette.primary.dark : alpha(theme.palette.primary.main, 0.04),
    borderColor: theme.palette.primary.main,
    color: active ? '#FFFFFF' : theme.palette.primary.main,
  },
  transition: 'all 0.2s ease',
}));

const ViewToggleButton = styled(IconButton)(({ theme, active }) => ({
  backgroundColor: active ? theme.palette.primary.main : 'transparent',
  color: active ? '#FFFFFF' : '#616161',
  border: `1px solid ${active ? theme.palette.primary.main : alpha('#9E9E9E', 0.3)}`,
  borderRadius: 0,
  padding: '10px',
  marginLeft: '8px',
  '&:hover': {
    backgroundColor: active ? theme.palette.primary.dark : alpha(theme.palette.primary.main, 0.04),
    borderColor: theme.palette.primary.main,
    color: active ? '#FFFFFF' : theme.palette.primary.main,
  },
  transition: 'all 0.2s ease',
}));

const Collection = () => {
  const [category, setCategory] = useState('all');
  const [viewMode, setViewMode] = useState('grid');
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('featured');
  const [filterMenuAnchor, setFilterMenuAnchor] = useState(null);
  const [activeFilters, setActiveFilters] = useState([]);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [displayedCoins, setDisplayedCoins] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    certified: 0,
    gold: 0,
    silver: 0,
    diamond: 0,
  });
  
  const itemsPerPage = 12;
  const { ref: loadMoreRef, inView } = useInView();

  // Calculate categories based on actual coin data
  const categories = [
    { label: 'All Coins', value: 'all', count: coins.length },
    { label: 'Australia', value: 'australia', count: coins.filter(c => c.category === 'australia').length },
    { label: 'Canada', value: 'canada', count: coins.filter(c => c.category === 'canada').length },
    { label: 'USA', value: 'usa', count: coins.filter(c => c.category === 'usa').length },
    { label: 'UK', value: 'uk', count: coins.filter(c => c.category === 'uk').length },
    { label: 'Gold', value: 'gold', count: coins.filter(c => c.metal === 'Gold').length },
    { label: 'Silver', value: 'silver', count: coins.filter(c => c.metal === 'Silver').length },
    { label: 'With Diamond', value: 'diamond', count: coins.filter(c => c.hasDiamond).length },
  ];

  const sortOptions = [
    { value: 'featured', label: 'Featured' },
    { value: 'newest', label: 'Newest First' },
    { value: 'price-asc', label: 'Price: Low to High' },
    { value: 'price-desc', label: 'Price: High to Low' },
    { value: 'grade-desc', label: 'Grade: High to Low' },
  ];

  const filterOptions = [
    { label: 'Certified Only', value: 'certified', icon: <StarIcon /> },
    { label: 'Diamond Inlaid', value: 'diamond', icon: <StarIcon /> },
    { label: 'Gold Coins', value: 'gold', icon: <TrendingUpIcon /> },
    { label: 'Premium Grade (Proof 70)', value: 'premium', icon: <StarIcon /> },
  ];

  useEffect(() => {
    // Calculate stats
    setStats({
      total: coins.length,
      certified: coins.filter(c => c.grade).length,
      gold: coins.filter(c => c.metal === 'Gold').length,
      silver: coins.filter(c => c.metal === 'Silver').length,
      diamond: coins.filter(c => c.hasDiamond).length,
    });
  }, []);

  useEffect(() => {
    // Reset and load initial coins when filters change
    const filtered = getFilteredCoins();
    setDisplayedCoins(filtered.slice(0, itemsPerPage));
    setPage(1);
  }, [category, search, activeFilters, sortBy]);

  useEffect(() => {
    // Load more coins when scrolling
    if (inView && !loading && displayedCoins.length < getFilteredCoins().length) {
      setLoading(true);
      setTimeout(() => {
        const filtered = getFilteredCoins();
        const start = displayedCoins.length;
        const end = Math.min(start + itemsPerPage, filtered.length);
        setDisplayedCoins(prev => [...prev, ...filtered.slice(start, end)]);
        setLoading(false);
      }, 500);
    }
  }, [inView, loading, displayedCoins.length]);

  const handleCategoryChange = (event, newValue) => {
    setCategory(newValue);
  };

  const handleFilterClick = (event) => {
    setFilterMenuAnchor(event.currentTarget);
  };

  const handleFilterClose = () => {
    setFilterMenuAnchor(null);
  };

  const handleFilterSelect = (filter) => {
    setActiveFilters(prev => 
      prev.includes(filter) 
        ? prev.filter(f => f !== filter)
        : [...prev, filter]
    );
  };

  const handleClearFilters = () => {
    setActiveFilters([]);
    setSearch('');
    setSortBy('featured');
    setCategory('all');
  };

  const handleScrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const getFilteredCoins = () => {
    let filtered = [...coins];

    // Apply category filter
    if (category !== 'all') {
      filtered = filtered.filter(coin => {
        if (category === 'gold') return coin.metal === 'Gold';
        if (category === 'silver') return coin.metal === 'Silver';
        if (category === 'diamond') return coin.hasDiamond === true;
        return coin.category?.toLowerCase() === category.toLowerCase();
      });
    }

    // Apply search filter
    if (search) {
      const searchLower = search.toLowerCase();
      filtered = filtered.filter(coin => 
        coin.name?.toLowerCase().includes(searchLower) ||
        coin.year?.toString().includes(searchLower) ||
        coin.grade?.toLowerCase().includes(searchLower) ||
        coin.metal?.toLowerCase().includes(searchLower) ||
        coin.mint?.toLowerCase().includes(searchLower)
      );
    }

    // Apply active filters
    activeFilters.forEach(filter => {
      switch(filter) {
        case 'certified':
          filtered = filtered.filter(coin => coin.grade);
          break;
        case 'diamond':
          filtered = filtered.filter(coin => coin.hasDiamond === true);
          break;
        case 'gold':
          filtered = filtered.filter(coin => coin.metal === 'Gold');
          break;
        case 'premium':
          filtered = filtered.filter(coin => coin.grade === 'Proof 70');
          break;
        default:
          break;
      }
    });

    // Apply sorting using numeric price
    switch(sortBy) {
      case 'price-asc':
        filtered.sort((a, b) => getNumericPrice(a) - getNumericPrice(b));
        break;
      case 'price-desc':
        filtered.sort((a, b) => getNumericPrice(b) - getNumericPrice(a));
        break;
      case 'newest':
        filtered.sort((a, b) => (parseInt(b.year) || 0) - (parseInt(a.year) || 0));
        break;
      case 'grade-desc':
        filtered.sort((a, b) => (b.gradeNum || 0) - (a.gradeNum || 0));
        break;
      default:
        // Featured - keep original order
        break;
    }

    return filtered;
  };

  const filteredCoins = getFilteredCoins();
  const totalPages = Math.ceil(filteredCoins.length / itemsPerPage);

  return (
    <Box sx={{ 
      py: 8,
      backgroundColor: '#F5F5F5',
      minHeight: '100vh',
    }}>
      <Container maxWidth="xl">
        {/* Breadcrumbs */}
        <Breadcrumbs 
          sx={{ mb: 4, color: '#9E9E9E' }}
          separator={<Box component="span" sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: '#9E9E9E' }} />}
        >
          <Link
            component={RouterLink}
            to="/"
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 0.5,
              color: '#9E9E9E',
              textDecoration: 'none',
              '&:hover': { color: 'primary.main' }
            }}
          >
            <HomeIcon sx={{ fontSize: 18 }} />
            Home
          </Link>
          <Typography color="#2D2D2D">Collection</Typography>
        </Breadcrumbs>

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography 
              variant="h6" 
              sx={{ 
                color: 'primary.main',
                mb: 2,
                letterSpacing: '0.3em',
                textTransform: 'uppercase',
              }}
            >
              Curated Selection
            </Typography>
            
            <Typography variant="h2" sx={{ color: '#2D2D2D', mb: 3 }}>
              The
              <Box component="span" sx={{ color: 'primary.main', mx: 2 }}>
                Collection
              </Box>
            </Typography>
            
            <Typography 
              variant="body1" 
              color="#616161" 
              sx={{ maxWidth: '700px', mx: 'auto' }}
            >
              Explore our curated selection of the world's finest rare coins, 
              each with documented provenance and certified authenticity.
            </Typography>
          </Box>

          {/* Stats Banner */}
          <Paper
            sx={{
              p: 3,
              mb: 6,
              backgroundColor: '#FFFFFF',
              border: '1px solid',
              borderColor: alpha('#9E9E9E', 0.15),
              boxShadow: '0 4px 12px rgba(0,0,0,0.02)',
            }}
          >
            <Grid container spacing={3}>
              {[
                { label: 'Total Coins', value: stats.total },
                { label: 'Certified', value: stats.certified },
                { label: 'Gold Coins', value: stats.gold },
                { label: 'Silver Coins', value: stats.silver },
                { label: 'With Diamonds', value: stats.diamond },
              ].map((stat, index) => (
                <Grid item xs={6} md={2.4} key={index}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h4" sx={{ color: 'primary.main', fontWeight: 600 }}>
                      {stat.value}
                    </Typography>
                    <Typography variant="body2" color="#616161">
                      {stat.label}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Paper>
        </motion.div>

        {/* Search and Filters Bar */}
        <Paper
          elevation={0}
          sx={{
            p: 3,
            mb: 6,
            backgroundColor: '#FFFFFF',
            border: '1px solid',
            borderColor: alpha('#9E9E9E', 0.15),
            boxShadow: '0 4px 12px rgba(0,0,0,0.02)',
          }}
        >
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={5}>
              <TextField
                fullWidth
                placeholder="Search by name, year, grade, metal, or mint..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: 'primary.main' }} />
                    </InputAdornment>
                  ),
                  endAdornment: search && (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setSearch('')}>
                        <CloseIcon sx={{ fontSize: 18, color: '#9E9E9E' }} />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#F5F5F5',
                    '&:hover': {
                      backgroundColor: '#FFFFFF',
                    },
                  },
                }}
              />
            </Grid>

            <Grid item xs={12} md={7}>
              <Box sx={{ 
                display: 'flex', 
                justifyContent: { xs: 'flex-start', md: 'flex-end' },
                alignItems: 'center',
                gap: 2,
                flexWrap: 'wrap',
              }}>
                {/* Filter Button */}
                <Button
                  variant="outlined"
                  startIcon={<FilterListIcon />}
                  onClick={handleFilterClick}
                  sx={{
                    borderColor: alpha('#9E9E9E', 0.3),
                    color: '#616161',
                    '&:hover': {
                      borderColor: 'primary.main',
                      color: 'primary.main',
                    },
                  }}
                >
                  Filters
                  {activeFilters.length > 0 && (
                    <Badge
                      badgeContent={activeFilters.length}
                      sx={{
                        ml: 1,
                        '& .MuiBadge-badge': {
                          backgroundColor: 'primary.main',
                          color: '#FFFFFF',
                          fontSize: '0.75rem',
                        },
                      }}
                    />
                  )}
                </Button>

                {/* Sort Select */}
                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <Select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    displayEmpty
                    startAdornment={<SortIcon sx={{ color: '#9E9E9E', mr: 1 }} />}
                    sx={{
                      backgroundColor: '#F5F5F5',
                      '& .MuiSelect-select': {
                        py: 1,
                      },
                    }}
                  >
                    {sortOptions.map(option => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {/* View Toggle */}
                <Box sx={{ display: 'flex' }}>
                  <Tooltip title="Grid View" arrow TransitionComponent={Zoom}>
                    <ViewToggleButton
                      active={viewMode === 'grid' ? 1 : 0}
                      onClick={() => setViewMode('grid')}
                    >
                      <ViewModuleIcon />
                    </ViewToggleButton>
                  </Tooltip>
                  <Tooltip title="Compact Grid" arrow TransitionComponent={Zoom}>
                    <ViewToggleButton
                      active={viewMode === 'compact' ? 1 : 0}
                      onClick={() => setViewMode('compact')}
                    >
                      <ViewComfyIcon />
                    </ViewToggleButton>
                  </Tooltip>
                  <Tooltip title="List View" arrow TransitionComponent={Zoom}>
                    <ViewToggleButton
                      active={viewMode === 'list' ? 1 : 0}
                      onClick={() => setViewMode('list')}
                    >
                      <ViewStreamIcon />
                    </ViewToggleButton>
                  </Tooltip>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {/* Categories Tabs */}
        <StyledTabs
          value={category}
          onChange={handleCategoryChange}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
        >
          {categories.map((cat) => (
            <Tab 
              key={cat.value} 
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <span>{cat.label}</span>
                  <Chip
                    label={cat.count}
                    size="small"
                    sx={{
                      backgroundColor: cat.value === category ? '#FFFFFF' : alpha('#9E9E9E', 0.1),
                      color: cat.value === category ? 'primary.main' : '#616161',
                      fontSize: '0.75rem',
                      height: 20,
                    }}
                  />
                </Box>
              } 
              value={cat.value} 
            />
          ))}
        </StyledTabs>

        {/* Active Filters */}
        <AnimatePresence>
          {(activeFilters.length > 0 || search || category !== 'all') && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              <Box sx={{ display: 'flex', gap: 1, mb: 4, flexWrap: 'wrap', alignItems: 'center' }}>
                <Typography variant="body2" color="#616161">
                  Active Filters:
                </Typography>
                
                {category !== 'all' && (
                  <StyledChip
                    label={`Category: ${categories.find(c => c.value === category)?.label}`}
                    onDelete={() => setCategory('all')}
                    active={1}
                  />
                )}
                
                {search && (
                  <StyledChip
                    label={`Search: ${search}`}
                    onDelete={() => setSearch('')}
                    active={1}
                  />
                )}
                
                {activeFilters.map(filter => (
                  <StyledChip
                    key={filter}
                    label={filterOptions.find(f => f.value === filter)?.label}
                    onDelete={() => handleFilterSelect(filter)}
                    active={1}
                  />
                ))}
                
                <Button
                  size="small"
                  onClick={handleClearFilters}
                  sx={{
                    color: '#616161',
                    '&:hover': { color: 'primary.main' },
                  }}
                >
                  Clear All
                </Button>
              </Box>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Results Count */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="body2" color="#616161">
            Showing {displayedCoins.length} of {filteredCoins.length} results
          </Typography>
          {filteredCoins.length > 0 && (
            <Typography variant="caption" color="#9E9E9E">
              Page {page} of {totalPages}
            </Typography>
          )}
        </Box>

        {/* Coins Grid */}
        {filteredCoins.length > 0 ? (
          <>
            <Grid container spacing={viewMode === 'compact' ? 2 : 4}>
              {displayedCoins.map((coin, index) => (
                <Grid 
                  item 
                  xs={12} 
                  sm={viewMode === 'list' ? 12 : 6} 
                  md={
                    viewMode === 'list' ? 12 : 
                    viewMode === 'compact' ? 3 : 4
                  } 
                  key={coin.id}
                >
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.05 }}
                  >
                    <CoinCard coin={coin} />
                  </motion.div>
                </Grid>
              ))}
            </Grid>

            {/* Load More / Pagination */}
            {displayedCoins.length < filteredCoins.length ? (
              <Box ref={loadMoreRef} sx={{ textAlign: 'center', mt: 8 }}>
                {loading ? (
                  <Grid container spacing={4}>
                    {[1, 2, 3].map((n) => (
                      <Grid item xs={12} md={4} key={n}>
                        <Skeleton 
                          variant="rectangular" 
                          height={400} 
                          sx={{ 
                            backgroundColor: alpha('#9E9E9E', 0.1),
                          }} 
                        />
                      </Grid>
                    ))}
                  </Grid>
                ) : (
                  <Button 
                    variant="outlined" 
                    size="large"
                    onClick={() => setPage(prev => prev + 1)}
                    endIcon={<ArrowUpwardIcon sx={{ transform: 'rotate(90deg)' }} />}
                    sx={{ 
                      px: 6, 
                      py: 2,
                      borderColor: alpha('#9E9E9E', 0.3),
                      color: '#616161',
                      '&:hover': {
                        borderColor: 'primary.main',
                        color: 'primary.main',
                        backgroundColor: alpha('#C6A75E', 0.04),
                      },
                    }}
                  >
                    Load More
                  </Button>
                )}
              </Box>
            ) : (
              displayedCoins.length > 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
                  <Pagination 
                    count={totalPages} 
                    page={page}
                    onChange={(e, val) => {
                      setPage(val);
                      const filtered = getFilteredCoins();
                      const start = (val - 1) * itemsPerPage;
                      const end = Math.min(start + itemsPerPage, filtered.length);
                      setDisplayedCoins(filtered.slice(start, end));
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }}
                    color="primary"
                    size="large"
                    sx={{
                      '& .MuiPaginationItem-root': {
                        color: '#616161',
                        '&.Mui-selected': {
                          backgroundColor: 'primary.main',
                          color: '#FFFFFF',
                          '&:hover': {
                            backgroundColor: 'primary.dark',
                          },
                        },
                      },
                    }}
                  />
                </Box>
              )
            )}
          </>
        ) : (
          // No Results State
          <Fade in={true}>
            <Paper
              sx={{
                p: 8,
                textAlign: 'center',
                backgroundColor: '#FFFFFF',
                border: '1px solid',
                borderColor: alpha('#9E9E9E', 0.15),
              }}
            >
              <SearchIcon sx={{ fontSize: 64, color: alpha('#9E9E9E', 0.3), mb: 3 }} />
              <Typography variant="h5" gutterBottom sx={{ color: '#2D2D2D' }}>
                No Coins Found
              </Typography>
              <Typography variant="body2" color="#616161" sx={{ mb: 4 }}>
                Try adjusting your search or filters to find what you're looking for.
              </Typography>
              <Button
                variant="contained"
                onClick={handleClearFilters}
              >
                Clear All Filters
              </Button>
            </Paper>
          </Fade>
        )}

        {/* Filter Menu */}
        <Menu
          anchorEl={filterMenuAnchor}
          open={Boolean(filterMenuAnchor)}
          onClose={handleFilterClose}
          PaperProps={{
            sx: {
              mt: 2,
              minWidth: 220,
              backgroundColor: '#FFFFFF',
              border: '1px solid',
              borderColor: 'primary.main',
            }
          }}
        >
          <Box sx={{ p: 2, borderBottom: `1px solid ${alpha('#9E9E9E', 0.15)}` }}>
            <Typography variant="subtitle2" sx={{ color: '#2D2D2D' }}>
              Filter By
            </Typography>
          </Box>
          {filterOptions.map((option) => (
            <MenuItem 
              key={option.value}
              onClick={() => handleFilterSelect(option.value)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                py: 1.5,
                backgroundColor: activeFilters.includes(option.value) ? alpha('#C6A75E', 0.08) : 'transparent',
                '&:hover': {
                  backgroundColor: alpha('#C6A75E', 0.04),
                },
              }}
            >
              <Box sx={{ color: activeFilters.includes(option.value) ? 'primary.main' : '#9E9E9E' }}>
                {option.icon}
              </Box>
              <Typography variant="body2" sx={{ flex: 1, color: '#2D2D2D' }}>
                {option.label}
              </Typography>
              {activeFilters.includes(option.value) && (
                <CheckIcon sx={{ color: 'primary.main', fontSize: 18 }} />
              )}
            </MenuItem>
          ))}
        </Menu>

        {/* Scroll to Top Button */}
        <Fade in={page > 1}>
          <Tooltip title="Scroll to top" arrow placement="left">
            <IconButton
              onClick={handleScrollToTop}
              sx={{
                position: 'fixed',
                bottom: 30,
                right: 30,
                backgroundColor: 'primary.main',
                color: '#FFFFFF',
                '&:hover': {
                  backgroundColor: 'primary.dark',
                },
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                zIndex: 1000,
              }}
            >
              <ArrowUpwardIcon />
            </IconButton>
          </Tooltip>
        </Fade>

        {/* Price Information Box */}
        <Box 
          sx={{ 
            mt: 6, 
            p: 3, 
            backgroundColor: alpha('#C6A75E', 0.05),
            border: '1px solid',
            borderColor: alpha('#C6A75E', 0.2),
            borderRadius: 2,
            textAlign: 'center'
          }}
        >
          <Typography variant="body2" color="#616161">
            <strong>Price Ranges:</strong> Prices shown are estimated market ranges based on current market conditions. 
            Final pricing depends on grade, certification, and market demand. Click "Inquire Now" for specific pricing and availability.
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default Collection;